import { ReactNode } from 'react';
import dayjs from 'dayjs';
import {
  GetBookingMyMscResponse,
  GetShopExcursionMyMSCResponse,
  MyMscExcursion,
  OBSI,
  PassengerI,
  PostBookingCardPrice,
  ShopOBSPassengerI,
} from '@msc-b2c/interfaces/interfaces';
import {
  AgeCategoryEnum,
  BookingStatusEnum,
  OBSTypesEnum,
  PriceBasisEnum,
} from '@msc-b2c/interfaces/enums';
import {
  breakpoints,
  getActiveSession,
  getNotAllowedPriceTypes,
  isPromotionPriceAvailable,
  roundPrice,
} from '@msc-b2c/shared-utils';
import { CartItem, CartPassenger, OBSObjI, ObsSecondaryTypes } from '@msc-b2c/interfaces/types';
import { getCartItemsForPrepaidCard } from '../../utils/myAreaCartUtils';
import { getCabinIdOfTheLoggedUser, isPassengerIdInLoggedUserCabin } from '../../utils/myAreaUtils';
import { CarouselCardData } from '../../productcarousel/ProductCarousel';

export const chunkArray = (arr: any, size: number) =>
  arr.reduce((acc, _, i) => (i % size ? acc : [...acc, arr.slice(i, i + size)]), []);

/** Matches `.postBookingCard`: 1 col ≤md, 2 cols md–lg, 3 cols ≥lg */
export function getCardsPerRowForViewport(width: number): number {
  if (width <= breakpoints.md) return 1;
  if (width < breakpoints.lg) return 2;
  return 3;
}

export const getDrawerContent = (
  order: string[],
  components: Record<string, ReactNode>
): ReactNode[] => {
  return order?.map((key) => components[key] ?? null);
};

export function isPerChildService(card: MyMscExcursion | OBSObjI | undefined): boolean {
  if (!card) {
    return false;
  }

  if (
    'applicability' in card &&
    card.applicability?.isApplicableForChild &&
    card.applicability?.isApplicableForJunior &&
    !card.applicability?.isApplicableForAdult &&
    !card.applicability?.isApplicableForSenior
  ) {
    return true;
  }

  return false;
}

/** Per-child price tier from a linked minor OBS package, when applicable. */
const getLinkedMinorChildPriceTier = (linkedMinorCard?: OBSObjI) => {
  if (!linkedMinorCard || !isPerChildService(linkedMinorCard)) {
    return undefined;
  }

  return linkedMinorCard.prices?.perChild?.total;
};

/**
 * Retrieves the price and corresponding label based on the price basis of the excursion card.
 *
 * @param {MyMscExcursion} card - The excursion card containing price information.
 * @param {string} perCabinLabel - The label to use if the price basis is per cabin.
 * @param {string} perAdultLabel - The label to use if the price basis is per person (adult/senior pricing).
 * @param perChildLabel
 * @returns {{ price: number | undefined, priceLabel: string }} - An object containing the price and the corresponding label.
 */
export function getPriceFromPriceBasis(
  card: MyMscExcursion | OBSObjI | undefined,
  perCabinLabel: string,
  perAdultLabel: string,
  perChildLabel?: string,
  disableRounding?: boolean
): { price: string; priceLabel: string | undefined } {
  const price = getPriceValue(card);
  const label = card?.priceBasis === PriceBasisEnum.PER_PERSON ? perAdultLabel : perCabinLabel;
  let isAdultAndSeniorApplicable = true;
  if (perChildLabel && 'applicability' in card && card.priceBasis === PriceBasisEnum.PER_PERSON) {
    isAdultAndSeniorApplicable =
      (card.applicability?.isApplicableForAdult || card.applicability?.isApplicableForSenior) ??
      true;
  }

  return {
    price: disableRounding ? price ?? '' : roundPrice(price),
    priceLabel: isAdultAndSeniorApplicable ? label : perChildLabel,
  };
}

/**
 * Creates a price object for post booking card display
 */
type PriceInfo = {
  afterDiscount: string | undefined;
  beforeDiscount: string | undefined;
  validFrom?: string;
  validTo?: string;
};

const createPriceObject = (
  priceInfo: PriceInfo,
  label: string | undefined,
  promotionPriceEnabled: boolean
): PostBookingCardPrice => {
  const afterDiscount = roundPrice(priceInfo.afterDiscount);
  const beforeDiscount = roundPrice(priceInfo.beforeDiscount);

  return {
    afterDiscount,
    beforeDiscount,
    label,
    isPromo: isPromotionPriceAvailable(
      promotionPriceEnabled,
      { afterDiscount, beforePromotionAndDiscount: beforeDiscount },
      priceInfo.validFrom,
      priceInfo.validTo
    ),
  };
};

/**
 * Retrieves array of prices for a card based on the card type and price basis.
 * @param options - An object containing the card, promotion price enabled flag, labels for per cabin, per adult, and per child.
 * @return {PostBookingCardPrice[]} - An array of PostBookingCardPrice objects containing price information and labels.
 */
export function getPostBookingCardPrices(options: {
  card: MyMscExcursion | OBSObjI | undefined;
  promotionPriceEnabled: boolean;
  showSinglePromotionPrice: boolean;
  perCabinLabel: string;
  perAdultLabel: string;
  perChildLabel?: string;
  /**
   * Minor ALLINC package linked to an adult card. When present, its per-child price is shown
   * alongside the adult price (the minor card itself is hidden from results).
   */
  linkedMinorCard?: OBSObjI;
}): PostBookingCardPrice[] {
  const {
    card,
    promotionPriceEnabled,
    showSinglePromotionPrice,
    perCabinLabel,
    perAdultLabel,
    perChildLabel,
    linkedMinorCard,
  } = options;

  if (!card) {
    return [];
  }

  if (isPerChildService(card) && card.priceBasis === PriceBasisEnum.PER_PERSON) {
    return [
      createPriceObject(
        {
          afterDiscount: card.prices?.perChild?.total?.afterDiscount,
          beforeDiscount: card.prices?.perChild?.total?.beforeDiscount,
          validFrom: card.promotion?.validFrom,
          validTo: card.promotion?.validTo,
        },
        perChildLabel,
        promotionPriceEnabled
      ),
    ];
  }

  if (card.priceBasis === PriceBasisEnum.PER_CABIN) {
    return [
      createPriceObject(
        {
          afterDiscount: card.prices?.perCabin?.total?.afterDiscount,
          beforeDiscount: card.prices?.perCabin?.total?.beforeDiscount,
          validFrom: card.promotion?.validFrom,
          validTo: card.promotion?.validTo,
        },
        perCabinLabel,
        promotionPriceEnabled
      ),
    ];
  }

  const prices: PostBookingCardPrice[] = [
    createPriceObject(
      {
        afterDiscount: card.prices?.perAdult?.total?.afterDiscount,
        beforeDiscount: card.prices?.perAdult?.total?.beforeDiscount,
        validFrom: card.promotion?.validFrom,
        validTo: card.promotion?.validTo,
      },
      perAdultLabel,
      promotionPriceEnabled
    ),
  ];

  const linkedMinorChildPrice = getLinkedMinorChildPriceTier(linkedMinorCard);

  // Only combined all-inclusive OBS packages show a second price: the linked hidden minor package.
  if (perChildLabel && linkedMinorChildPrice?.afterDiscount) {
    prices.push(
      createPriceObject(
        {
          afterDiscount: linkedMinorChildPrice.afterDiscount,
          beforeDiscount: linkedMinorChildPrice.beforeDiscount,
          validFrom: linkedMinorCard?.promotion?.validFrom,
          validTo: linkedMinorCard?.promotion?.validTo,
        },
        perChildLabel,
        promotionPriceEnabled
      )
    );
  }

  return prices;
}

export function getPriceValue(card: MyMscExcursion | OBSObjI | undefined): string | undefined {
  if (!card) {
    return undefined;
  }

  if (isPerChildService(card) && card.priceBasis === PriceBasisEnum.PER_PERSON) {
    return card.prices?.perChild?.total?.afterDiscount;
  }

  return card.priceBasis === PriceBasisEnum.PER_PERSON
    ? card.prices?.perAdult?.total?.afterDiscount
    : card.prices?.perCabin?.total?.afterDiscount;
}

/**
 * Retrieves the names of passengers who have already booked an excursion.
 *
 * @param card
 * @param {GetBookingMyMscResponse} booking - The booking response containing passenger information.
 * @param isExcursion
 * @param allCabins - indicates whether all cabins should be considered (for multi-cabin booking).
 * @returns {string[]} - An array of names of passengers who have already booked the excursion.
 */
export function getAlreadyBookedPassengerId(
  card: MyMscExcursion | OBSObjI | CarouselCardData,
  booking: GetBookingMyMscResponse,
  isExcursion: boolean,
  allCabins?: boolean
): string[] {
  if (!booking.prepaid?.bookings) {
    return [];
  }

  // Multi-cruise scneario filtering only consider prepaid items for the active cruise
  const activeCruiseId = getActiveSession().cruiseId;
  const prepaidBookings = activeCruiseId
    ? booking.prepaid.bookings.filter(
        (currentActiveCruiseBooking) => currentActiveCruiseBooking.cruise.id === activeCruiseId
      )
    : booking.prepaid.bookings;

  const cardStartDate = isExcursion && 'startDate' in card ? card.startDate : undefined;
  const hasSameDateIgnoringTime = (leftDate?: string, rightDate?: string): boolean => {
    if (!leftDate || !rightDate) {
      return false;
    }

    const left = dayjs(leftDate);
    const right = dayjs(rightDate);

    if (!left.isValid() || !right.isValid()) {
      return false;
    }

    return left.isSame(right, 'day');
  };

  return prepaidBookings
    .flatMap((currentActiveCruiseBooking) => {
      // select the right array of items based on the card type
      const items = isExcursion
        ? currentActiveCruiseBooking.excursions
        : currentActiveCruiseBooking.onboardServices;

      // if items is undefined, return an empty array
      if (!items) {
        return [];
      }

      if (isExcursion) {
        return items.filter((item) => {
          const excursionItem = item as { startDate?: string };

          return (
            item.bookingStatus?.code === BookingStatusEnum.BOOKED &&
            item.code === card?.code &&
            hasSameDateIgnoringTime(excursionItem.startDate, cardStartDate)
          );
        });
      }

      return items.filter(
        (item) => item.bookingStatus?.code === BookingStatusEnum.BOOKED && item.code === card?.code
      );
    })
    .flatMap((item) => {
      // if price is per person, return the passenger ID
      const cabinItemIdOfLoggedUser = getCabinIdOfTheLoggedUser(booking) ?? '';
      const bookedCabinId = item.cabin?.id ?? '';
      let isAllInc = false;
      if ('prepaidItems' in card) {
        isAllInc =
          Boolean(card.prepaidItems?.isAllInclusive) || card?.type?.code === OBSTypesEnum.BEVERAGES;
      }
      if (item.priceBasis === PriceBasisEnum.PER_PERSON) {
        return isPassengerIdInLoggedUserCabin(
          booking,
          cabinItemIdOfLoggedUser,
          item.passenger.id
        ) ||
          isAllInc ||
          allCabins
          ? [item.passenger.id]
          : [];
      } else {
        // price is per cabin, so we need to get the passenger IDs from the cabin
        return allCabins || bookedCabinId === cabinItemIdOfLoggedUser
          ? getPassengerIdInCabin(booking, bookedCabinId, isAllInc)?.filter(
              (id): id is string => id !== undefined
            )
          : [];
      }
    });
}

/**
 * Retrieves selected guide language of a passenger, that has booked an excursion earlier.
 *
 * @param cardCode - The code of a selected excursion
 * @param {GetBookingMyMscResponse} booking - The booking response containing passenger information.
 * @returns {string} - The first found selected guide language of an already booked passenger.
 */
export function getBookedPassengerLanguage(
  cardCode: string,
  booking: GetBookingMyMscResponse,
  startDate?: string
): string {
  if (!booking.prepaid?.bookings) {
    return '';
  }

  // Multi-cruise support: only consider prepaid items for the active cruise
  const activeCruiseId = getActiveSession().cruiseId;
  const prepaidBookings = activeCruiseId
    ? booking.prepaid.bookings.filter(
        (currentActiveCruiseBooking) => currentActiveCruiseBooking.cruise.id === activeCruiseId
      )
    : booking.prepaid.bookings;

  for (const currentActiveCruiseBooking of prepaidBookings) {
    const item = currentActiveCruiseBooking.excursions?.find((item) => {
      if (item.code !== cardCode || item.bookingStatus?.code !== BookingStatusEnum.BOOKED) {
        return false;
      }
      if (!startDate) {
        return true;
      }

      return item.startDate === startDate;
    });
    if (item?.preferredGuideLanguage) {
      return item.preferredGuideLanguage;
    }
  }

  return '';
}

/**
 * Retrieves the names of passengers who have already booked a prepaid item.
 *
 * @param {string[]} passengerIds - An array of passenger IDs who have booked the prepaid item.
 * @param {GetBookingMyMscResponse} booking - The booking response containing passenger information.
 * @returns {string[]} - An array of names of passengers who have already booked the prepaid item.
 */
export function getNamesOfAlreadyBookedPassengers(
  passengerIds: string[],
  booking: GetBookingMyMscResponse
): string[] {
  if (!passengerIds.length || !booking.passengers?.length) {
    return [];
  }

  // return the names of passengers who have already booked the excursion
  return booking.passengers
    .filter((passenger) => passengerIds.includes(passenger.id))
    .map((passenger) => ` ${passenger.name} ${passenger.surname}`);
}

/**
 * Retrieves the IDs of passengers in a specific cabin.
 *
 * @param {GetBookingMyMscResponse} booking - The booking response containing cabin and passenger information.
 * @param {string} cabinId - The ID of the cabin to retrieve passenger IDs from.
 * @param isAllInclusive
 * @returns {(string | undefined)[] | undefined} - An array of passenger IDs in the specified cabin, or an empty array if the cabin is not found, or all passengers in the booking if the item is allInclusive.
 */
export function getPassengerIdInCabin(
  booking: GetBookingMyMscResponse,
  cabinId: string,
  isAllInclusive: boolean
): (string | undefined)[] | undefined {
  if (!isAllInclusive) {
    const cabin = booking.cabins.find((cabin) => cabin.id === cabinId);
    if (!cabin) {
      return [];
    }
    return cabin.passengers?.map((passenger) => passenger.id);
  } else {
    return booking.passengers?.map((passenger) => passenger.id);
  }
}

/**
 * Retrieves the names of passengers who have not yet booked the prepaid item.
 *
 * @param {string[]} passengerIds - An array of passenger IDs who have booked the prepaid item.
 * @param {GetBookingMyMscResponse} booking - The booking response containing passenger information.
 * @param {string} activeCabinId - Cabin ID of a currently logged in user.
 * @returns {string[]} - An array of names of passengers who have not yet booked the prepaid item.
 */
export function checkAllTheOtherEligiblePassengers(
  passengerIds: string[],
  booking: GetBookingMyMscResponse,
  activeCabinId?: string
): string[] {
  if (!passengerIds.length || !booking.passengers?.length) {
    return [];
  }

  const activeCabinPassengers = booking.cabins.find(
    (cabin) => cabin.id === activeCabinId
  )?.passengers;

  // return the list of passengers who haven't booked the prepaid yet
  return booking.passengers
    .filter(
      (passenger) =>
        !activeCabinId ||
        activeCabinPassengers?.find(
          (activeCabinPassenger) => activeCabinPassenger.id === passenger.id
        )
    )
    .filter((passenger) => !passengerIds.includes(passenger.id))
    .map((passenger) => `${passenger.name} ${passenger.surname}`);
}

/**
 * Checks if the price type inside one cabin is not allowed to purchase prepaids.
 *
 * @param {GetBookingMyMscResponse} booking - The booking response containing cabin information.
 * @returns {boolean} - Returns true if the price type is not allowed, otherwise false.
 */
export function isPriceTypeNotAllowed(booking: GetBookingMyMscResponse): boolean {
  // Check if the price type inside one cabin is not allowed to purchase prepaids
  return booking.cabins.some((cabin) => getNotAllowedPriceTypes().includes(cabin.priceType));
}

/**
 * Determines whether the display button should be shown based on booking and booked passengers.
 *
 * @param {string[]} bookedPassengers - An array of passenger IDs who have already booked.
 * @param {GetBookingMyMscResponse} booking - The booking response containing booking information.
 * @param {string} activeCabinId - Cabin ID of a currently logged in user.
 * @returns {boolean} - Returns true if the display button should be shown, otherwise false.
 */
export function getDisplayButton(
  bookedPassengers: string[],
  booking?: GetBookingMyMscResponse,
  activeCabinId?: string
): boolean {
  if (!booking) {
    return false;
  }

  const isButtonAvailableForPax =
    bookedPassengers.length === 0 ||
    checkAllTheOtherEligiblePassengers(bookedPassengers, booking, activeCabinId).length > 0;
  return isPriceTypeNotAllowed(booking) ? false : isButtonAvailableForPax;
}

/**
 * Determines whether a prepaid item still has at least one passenger slot available to book.
 *
 * Returns `true` when no passengers have booked the item yet, or when at least one eligible
 * passenger in the active cabin has not yet booked it. Returns `false` if the booking is
 * missing or every eligible passenger is already booked.
 *
 * @param {string[]} bookedPassengers - IDs of passengers who have already booked this item.
 * @param {GetBookingMyMscResponse} [booking] - The booking response containing passenger and cabin data.
 * @param {string} [activeCabinId] - The cabin ID of the currently logged-in user; used to scope
 *   eligibility checks to the relevant cabin.
 * @returns {boolean} `true` if the item is not fully booked, `false` otherwise.
 *
 * @see checkAllTheOtherEligiblePassengers
 */
export function isItemNotFullyBooked(
  bookedPassengers: string[],
  booking?: GetBookingMyMscResponse,
  activeCabinId?: string
): boolean {
  if (!booking) {
    return false;
  }

  return (
    bookedPassengers.length === 0 ||
    checkAllTheOtherEligiblePassengers(bookedPassengers, booking, activeCabinId).length > 0
  );
}

/**
 * Retrieves the image URL for a given card.
 *
 * @param {any} card - The card object containing image information.
 * @param {string} urlCountry - The base URL for the country.
 * @returns {string} - The URL of the card image.
 */
export function getCardImage(card: any, urlCountry: string): string {
  if (card?.prepaidItems?.optimisedImage) {
    return card.prepaidItems.optimisedImage;
  }
  if (card?.prepaidItems?.image) {
    return card.prepaidItems.image;
  }
  if (card?.images?.desktop?.optimisedImage) {
    return card.images.desktop.optimisedImage;
  }
  if (card?.images?.image) {
    return card.images.image;
  }
  if (card?.assets?.[0]?.excursion?.optimisedImage) {
    return card.assets[0].excursion.optimisedImage;
  }
  if (card?.assets?.[0]?.excursion?.image) {
    return card.assets[0].excursion.image;
  }
  if (card?.code) {
    return `${urlCountry}/${card.code}.jpg`;
  }
  return '';
}

/**
 * Retrieves the package name label based on the service type and OBS type configured on CMS as service type.
 *
 * @param {string | undefined} serviceType - The service type to match against the OBS service types.
 * @param {ObsSecondaryTypes[]} obsSecondaryType - An array of OBS service types.
 * @returns {string} - The package name label corresponding to the service type, or the first package name label if no match is found.
 */
export function getPackageNameLabelObsBasedOnType(
  serviceType: string | undefined,
  obsSecondaryType: ObsSecondaryTypes[]
): string {
  let packageNameLabel = obsSecondaryType[0].packageNameLabel;
  if (serviceType) {
    packageNameLabel =
      obsSecondaryType.find((type: ObsSecondaryTypes) => type.type === serviceType)
        ?.packageNameLabel ?? packageNameLabel;
  }
  return packageNameLabel;
}

/**
 * Checks if a passenger is an adult or senior based on their age category
 * @param passenger - The passenger object to check
 * @returns boolean - True if the passenger is an adult or senior, false otherwise
 */
export function isAdultOrSenior(passenger: PassengerI): boolean {
  return (
    passenger.ageCategory === AgeCategoryEnum.ADULT ||
    passenger.ageCategory === AgeCategoryEnum.SENIOR
  );
}

/**
 * Calculates if a passenger will be under small kid threshold age at the time of departure
 * @param passenger - The passenger object containing date of birth
 * @param smallKidAgeThreshold - The threshold age in years
 * @param bookingData - The booking data containing departure date
 * @returns boolean - True if passenger will be under small kid threshold age at departure, false otherwise
 */
export function isUnderSmallKidAgeThreshold(
  passenger: PassengerI,
  smallKidAgeThreshold: number,
  bookingData?: GetBookingMyMscResponse
): boolean {
  if (!bookingData?.departureDate || !passenger.dateOfBirth) {
    return false;
  }

  const MS_IN_YEAR = 1000 * 60 * 60 * 24 * 365.25;

  const departureDate = new Date(bookingData.departureDate);
  const birthDate = new Date(passenger.dateOfBirth);
  const diffInYears = (departureDate.getTime() - birthDate.getTime()) / MS_IN_YEAR;

  return diffInYears < smallKidAgeThreshold;
}

type PrepaidItemApplicability = {
  isApplicableForSenior?: boolean;
  isApplicableForAdult?: boolean;
  isApplicableForChild?: boolean;
  isApplicableForJunior?: boolean;
  isApplicableForInfant?: boolean;
};

type FlatAgeRequirements = {
  minimum?: number;
  maximum?: number;
};

const isFlatAgeRequirements = (ageRequirements: unknown): ageRequirements is FlatAgeRequirements =>
  ageRequirements != null &&
  typeof ageRequirements === 'object' &&
  ('minimum' in ageRequirements || 'maximum' in ageRequirements);

export function isPassengerRestrictedByItemAgeRequirements(
  passenger: PassengerI,
  ageRequirements?: unknown
): boolean {
  if (!isFlatAgeRequirements(ageRequirements)) {
    return false;
  }

  const passengerAge = passenger.age ?? 0;

  return (
    (ageRequirements.minimum !== undefined && passengerAge < ageRequirements.minimum) ||
    (ageRequirements.maximum !== undefined && passengerAge > ageRequirements.maximum)
  );
}

export function isPassengerApplicableForPrepaidItem(
  passenger: PassengerI,
  card: MyMscExcursion | OBSObjI,
  isExcursion: boolean
): boolean {
  if ('applicability' in card && card.applicability) {
    const applicability = card.applicability as PrepaidItemApplicability;

    switch (passenger.ageCategory) {
      case AgeCategoryEnum.ADULT:
        return applicability.isApplicableForAdult !== false;
      case AgeCategoryEnum.SENIOR:
        return applicability.isApplicableForSenior !== false;
      case AgeCategoryEnum.CHILD:
        return applicability.isApplicableForChild !== false;
      case AgeCategoryEnum.JUNIOR:
        return applicability.isApplicableForJunior !== false;
      case AgeCategoryEnum.INFANT:
        return applicability.isApplicableForInfant !== false;
      default:
        return true;
    }
  }

  if (!isExcursion) {
    return true;
  }

  const excursionCard = card as MyMscExcursion;

  if (excursionCard.priceBasis === PriceBasisEnum.PER_CABIN) {
    return true;
  }

  const prices = excursionCard.prices;
  const hasPriceTier =
    prices?.perAdult ||
    prices?.perSenior ||
    prices?.perChild ||
    prices?.perJunior ||
    prices?.perInfant;

  if (!hasPriceTier) {
    return true;
  }

  switch (passenger.ageCategory) {
    case AgeCategoryEnum.ADULT:
      return Boolean(prices?.perAdult);
    case AgeCategoryEnum.SENIOR:
      return Boolean(prices?.perSenior);
    case AgeCategoryEnum.CHILD:
      return Boolean(prices?.perChild);
    case AgeCategoryEnum.JUNIOR:
      return Boolean(prices?.perJunior);
    case AgeCategoryEnum.INFANT:
      return Boolean(prices?.perInfant);
    default:
      return true;
  }
}

export function isPassengerAddonEligibleForPrepaidItem(
  passenger: PassengerI,
  card: MyMscExcursion | OBSObjI,
  isExcursion: boolean
): boolean {
  const ageRequirements =
    !isExcursion && 'ageRequirements' in card ? card.ageRequirements : undefined;

  return (
    isPassengerApplicableForPrepaidItem(passenger, card, isExcursion) &&
    !isPassengerRestrictedByItemAgeRequirements(passenger, ageRequirements)
  );
}

export function getAddonEligiblePassengersInCabin(
  card: MyMscExcursion | OBSObjI,
  booking: GetBookingMyMscResponse,
  cabinId: string | undefined,
  isExcursion: boolean
): PassengerI[] {
  return getCabinPassengers(booking, cabinId).filter(
    (passenger): passenger is PassengerI & { id: string } =>
      Boolean(passenger.id) && isPassengerAddonEligibleForPrepaidItem(passenger, card, isExcursion)
  );
}

export function getPassengerIdsInCartForItemInCabin(
  card: MyMscExcursion | OBSObjI,
  cartItems: CartItem[] | undefined,
  booking: GetBookingMyMscResponse,
  cabinId: string | undefined
): Set<string> {
  if (!cabinId || !cartItems?.length) {
    return new Set();
  }

  const cabinPassengerIds = new Set(
    getCabinPassengers(booking, cabinId)
      .map((passenger) => passenger.id)
      .filter((id): id is string => Boolean(id))
  );

  const passengerIdsInCart = new Set<string>();
  const matchingCartItems = getCartItemsForPrepaidCard(card, cartItems);

  matchingCartItems.forEach((cartItem) => {
    if (cartItem.priceBasis === PriceBasisEnum.PER_CABIN) {
      const isCabinInCart = cartItem.cabins?.some((cabin) => cabin.id === cabinId);
      if (!isCabinInCart) {
        return;
      }
    }

    cartItem.passengers?.forEach((passenger) => {
      if (passenger.id && cabinPassengerIds.has(passenger.id)) {
        passengerIdsInCart.add(passenger.id);
      }
    });
  });

  return passengerIdsInCart;
}

export const getPassengerIdsFromSelectionExcludingPassengerIds = (
  selection: Record<string, Record<string, boolean>> | null | undefined,
  excludePassengerIds: Set<string>
) =>
  Object.values(selection ?? {}).flatMap((cabinPassengers) =>
    Object.entries(cabinPassengers)
      .filter(([passengerId, isChecked]) => isChecked && !excludePassengerIds.has(passengerId))
      .map(([passengerId]) => passengerId)
  );

export const hasPassengerSelectionExcludingPassengerIds = (
  selection: Record<string, Record<string, boolean>> | null | undefined,
  excludePassengerIds: Set<string>
) => getPassengerIdsFromSelectionExcludingPassengerIds(selection, excludePassengerIds).length > 0;

export function hasRemainingAddonEligiblePassengersInCabin(
  card: MyMscExcursion | OBSObjI,
  booking: GetBookingMyMscResponse | undefined,
  cabinId: string | undefined,
  isExcursion: boolean,
  cartItems?: CartItem[]
): boolean {
  if (!booking || !cabinId) {
    return false;
  }

  const bookedPassengerIds = new Set(
    getAlreadyBookedPassengerId(card, booking, isExcursion, false)
  );
  const cartPassengerIds = getPassengerIdsInCartForItemInCabin(card, cartItems, booking, cabinId);

  return getAddonEligiblePassengersInCabin(card, booking, cabinId, isExcursion).some(
    (passenger) =>
      passenger.id && !bookedPassengerIds.has(passenger.id) && !cartPassengerIds.has(passenger.id)
  );
}

export function areAllAddonEligiblePassengersBookedInCabin(
  card: MyMscExcursion | OBSObjI,
  booking: GetBookingMyMscResponse | undefined,
  cabinId: string | undefined,
  isExcursion: boolean
): boolean {
  if (!booking || !cabinId) {
    return false;
  }

  const addonEligiblePassengers = getAddonEligiblePassengersInCabin(
    card,
    booking,
    cabinId,
    isExcursion
  );

  if (!addonEligiblePassengers.length) {
    return false;
  }

  const bookedPassengerIds = new Set(
    getAlreadyBookedPassengerId(card, booking, isExcursion, false)
  );

  return addonEligiblePassengers.every(
    (passenger) => passenger.id && bookedPassengerIds.has(passenger.id)
  );
}

/** True when every add-on eligible passenger in the cabin is already booked or in the cart. */
export function areAllAddonEligiblePassengersAccountedForInCabin(
  card: MyMscExcursion | OBSObjI,
  booking: GetBookingMyMscResponse | undefined,
  cabinId: string | undefined,
  isExcursion: boolean,
  cartItems?: CartItem[]
): boolean {
  if (!booking || !cabinId) {
    return false;
  }

  const addonEligiblePassengers = getAddonEligiblePassengersInCabin(
    card,
    booking,
    cabinId,
    isExcursion
  );

  if (!addonEligiblePassengers.length) {
    return false;
  }

  const bookedPassengerIds = new Set(
    getAlreadyBookedPassengerId(card, booking, isExcursion, false)
  );
  const cartPassengerIds = getPassengerIdsInCartForItemInCabin(card, cartItems, booking, cabinId);

  return addonEligiblePassengers.every(
    (passenger) =>
      passenger.id && (bookedPassengerIds.has(passenger.id) || cartPassengerIds.has(passenger.id))
  );
}

/**
 * Determines if there are no eligible passengers available to add to an Excursion.
 *
 * @param card - The Excursion card object containing service details and age requirements
 * @param shopExcursion - The shop Excursion data containing price and applicability info
 * @param cabinId - The ID of a cabin to check its passengers for eligibility
 * @param booking - The booking response containing passenger data
 *
 * @returns boolean - true if all passengers are either already booked OR not eligible (restricted by age,
 *          not applicable, or other restrictions). Returns false if at least one passenger is
 *          eligible and available to add to the service.
 */
export function isNoEligiblePassengersForExcursion(
  card: MyMscExcursion | null,
  shopExcursion: GetShopExcursionMyMSCResponse | null,
  cabinId?: string,
  booking?: GetBookingMyMscResponse
): boolean {
  if (!card || !booking) {
    return false;
  }

  const cabinPassengers = getCabinPassengers(booking, cabinId);
  if (cabinPassengers.length === 0) {
    return true;
  }

  const alreadyBookedPaxID = getAlreadyBookedPassengerId(card, booking, true);
  const ageReq = shopExcursion?.ageRequirements;

  const allPassengersNotEligible = cabinPassengers.every((passenger) => {
    if (!passenger.id) {
      return true;
    }

    const passengerAge = passenger.age ?? 0;

    const isRestrictedByAgeRequirements =
      (ageReq?.minimum !== undefined && passengerAge < ageReq.minimum) ||
      (ageReq?.maximum !== undefined && passengerAge > ageReq.maximum);

    const excursionFullPassengerData = shopExcursion?.prices?.perPassenger.find(
      (px) => px.id === passenger.id
    );

    const isApplicable = excursionFullPassengerData?.isApplicable;

    return (
      isRestrictedByAgeRequirements || alreadyBookedPaxID?.includes(passenger.id) || !isApplicable
    );
  });

  return allPassengersNotEligible;
}

/**
 * Determines if the passenger is eligible for the selected excursion.
 *
 * @param card - The Excursion card object containing details and age requirements
 * @param shopExcursion - The shop Excursion data containing price and applicability info
 * @param booking - The booking response containing passenger data
 * @param passenger - The passenger to check for eligibility
 *
 * @returns boolean - true if the passenger is eligible for the excursion. Returns false otherwise.
 */
export function isPassengerExcursionEligible(
  card: MyMscExcursion | null,
  shopExcursion: GetShopExcursionMyMSCResponse | null,
  booking?: GetBookingMyMscResponse,
  passenger?: PassengerI
): boolean {
  if (!card || !booking || !passenger) {
    return false;
  }

  const alreadyBookedPaxID = getAlreadyBookedPassengerId(card, booking, true);
  const ageReq = shopExcursion?.ageRequirements;
  const passengerAge = passenger.age ?? 0;

  const isRestrictedByAgeRequirements =
    (ageReq?.minimum !== undefined && passengerAge < ageReq.minimum) ||
    (ageReq?.maximum !== undefined && passengerAge > ageReq.maximum);

  const excursionFullPassengerData = shopExcursion?.prices?.perPassenger.find(
    (px) => px.id === passenger.id
  );

  const isApplicable = excursionFullPassengerData?.isApplicable;

  return (
    !isRestrictedByAgeRequirements && !alreadyBookedPaxID?.includes(passenger.id) && isApplicable
  );
}

/**
 * Determines if there are no eligible passengers available to add to an OBS.
 *
 * @param card - The OBS card object containing service details and age requirements
 * @param shopOBS - The shop OBS data containing price and applicability info
 * @param cabinId - The ID of a cabin to check its passengers for eligibility
 * @param booking - The booking response containing passenger data
 *
 * @returns boolean - true if all passengers are either already booked OR not eligible (restricted by age,
 *          not applicable, or other restrictions). Returns false if at least one passenger is
 *          eligible and available to add to the service.
 */
export function isNoEligiblePassengersForOBS(
  card: OBSObjI | null,
  shopOBS: OBSI | null,
  cabinId?: string,
  booking?: GetBookingMyMscResponse
): boolean {
  if (!card || !booking) {
    return false;
  }

  const activeCabinPassengers = getCabinPassengers(booking, cabinId);
  if (activeCabinPassengers.length === 0) {
    return true;
  }

  const alreadyBookedPaxID = getAlreadyBookedPassengerId(card, booking, false);
  const ageReq = card.ageRequirements;

  const obsPassengerPrices =
    card?.code === shopOBS?.code ? shopOBS?.prices?.perPassenger ?? [] : [];

  const allPassengersNotEligible = activeCabinPassengers.every((passenger) => {
    if (!passenger.id) {
      return true;
    }

    const age = passenger.age ?? 0;

    const isAlreadyBooked = alreadyBookedPaxID?.includes(passenger.id);
    if (isAlreadyBooked) {
      return true;
    }

    const isRestrictedByAge =
      (ageReq?.minimum !== undefined && age < ageReq.minimum) ||
      (ageReq?.maximum !== undefined && age > ageReq.maximum);
    const passengerShopData = obsPassengerPrices.find((px) => px.id === passenger.id);
    const isNotApplicable = passengerShopData?.isApplicable === false;

    return isRestrictedByAge || isNotApplicable;
  });

  return allPassengersNotEligible;
}

/** OBS combined / all-inc packages that shop multiple related items and merge INT135 results. */
export function hasObsCombinedRelatedPackages(card: OBSObjI | null): boolean {
  if (!card) {
    return false;
  }

  return (
    (card.prepaidItems?.serviceCodes?.adults?.length ?? 0) > 0 ||
    (card.prepaidItems?.serviceCodes?.minors?.length ?? 0) > 0
  );
}

/**
 * Scenario 1 (pax not applicable): after a combined OBS shop merge, {@link ShopOBSPassengerI.isApplicable}
 * is true if the passenger is eligible on any linked package. Passengers only eligible on another
 * linked item (see {@link ShopOBSPassengerI.relatedItemCode}) must not trigger the banner.
 */
export function isObsPassengerNotApplicableForPaxNotEligibleBanner(
  passenger: Pick<ShopOBSPassengerI, 'isApplicable' | 'relatedItemCode'>,
  isCombinedObsPackage: boolean
): boolean {
  if (passenger.isApplicable === false) {
    return true;
  }

  if (isCombinedObsPackage && passenger.isApplicable) {
    return false;
  }

  return false;
}

export function hasSomeObsPassengersNotApplicableForPaxNotEligibleBanner(
  passengers: Pick<ShopOBSPassengerI, 'isApplicable' | 'relatedItemCode'>[],
  isCombinedObsPackage: boolean
): boolean {
  return passengers.some((passenger) =>
    isObsPassengerNotApplicableForPaxNotEligibleBanner(passenger, isCombinedObsPackage)
  );
}

/**
 * Retrieves all passenger objects for a specific cabin from the booking data.
 *
 * @param booking - The booking response containing cabin and passenger information.
 * @param cabinId - The ID of the cabin to retrieve passengers from. If undefined, returns an empty array.
 * @returns PassengerI[] - An array of passenger objects in the specified cabin, or an empty array if the cabin is not found or cabinId is undefined.
 */
export function getCabinPassengers(booking: GetBookingMyMscResponse, cabinId: string | undefined) {
  const activeCabinPassengersIds: string[] = [];
  booking.cabins
    .filter((cabin) => cabin.id === cabinId)
    .map((cabin) =>
      cabin.passengers?.forEach(
        (passenger) => passenger.id && activeCabinPassengersIds.push(passenger.id)
      )
    );
  return (
    booking.passengers?.filter((passenger) => activeCabinPassengersIds.includes(passenger.id)) ?? []
  );
}

/**
 * Checks if a reservation for Post Booking is available for at least one passenger.
 *
 * @param card - The OBS or Excursion card object containing service details
 * @param isExcursion - Defines the card type
 * @param booking - The booking response containing passenger data
 * @param activeCabinId - Cabin ID of a currently logged in user.
 *
 * @returns boolean - true if selected Excursion/OBS does not have any passenger booked or there is at
 *          least one passenger that is able to make reservation. Returns false otherwise.
 */
export function isReservationAvailable(
  card: MyMscExcursion | OBSObjI | CarouselCardData,
  isExcursion: boolean,
  booking?: GetBookingMyMscResponse,
  activeCabinId?: string
): boolean {
  if (!booking) {
    return false;
  }

  const currentCardBookedPassengers = getAlreadyBookedPassengerId(card, booking, isExcursion);

  return (
    currentCardBookedPassengers.length === 0 ||
    checkAllTheOtherEligiblePassengers(currentCardBookedPassengers, booking, activeCabinId).length >
      0
  );
}

/**
 * Transforms CartPassenger type objects to Records with passengerId to preferredGuideLanguage pairs.
 *
 * @param {CartPassenger[]} passengers - An array of cart passenger data
 *
 * @returns Record<string, string> - Records of passengerId to preferredGuideLanguage pairs.
 */
export function passengersToRecord(passengers: CartPassenger[]): Record<string, string> {
  return Object.fromEntries(
    passengers
      .filter((passenger) => passenger.preferredGuideLanguage !== undefined)
      .map((passenger) => [passenger.id, passenger.preferredGuideLanguage ?? ''])
  );
}
